/**
 * OrgChart.js — Company Org Chart
 *
 * FIX: Employees with status = 'inactive' (On Hold) are now excluded
 *      from the tree. Only active employees appear.
 *
 * How: After fetching, filter list to status === 'active' before buildTree().
 * Stats count only active employees.
 */
import React, { useState, useEffect } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { Users, Search } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

// ── Avatar initials helper ────────────────────────────────────────────────────
const initials = (name = '') =>
    name.split(' ').slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('');

const ROLE_COLOURS = {
    'super-admin':       '#7c3aed',
    'admin':             '#4f46e5',
    'hr-manager':        '#0891b2',
    'account-manager':   '#059669',
    'reporting-manager': '#d97706',
    'recruiter':         '#db2777',
    'hiring-manager':    '#ea580c',
    'employee':          '#64748b',
};

// ── Build tree from flat list ─────────────────────────────────────────────────
const buildTree = (employees) => {
    const map   = {};
    const roots = [];
    employees.forEach(e => { map[e.id] = { ...e, children: [] }; });
    employees.forEach(e => {
        if (e.manager_id && map[e.manager_id]) {
            map[e.manager_id].children.push(map[e.id]);
        } else {
            roots.push(map[e.id]);
        }
    });
    return roots;
};

// ── Single node card ──────────────────────────────────────────────────────────
const OrgNode = ({ node, depth = 0, searchTerm = '' }) => {
    const [expanded, setExpanded] = useState(depth < 2);
    const hasChildren = node.children?.length > 0;
    const roleColor   = ROLE_COLOURS[node.role] || '#64748b';
    const highlighted = searchTerm && (
        node.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.department?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Node card */}
            <div
                style={{
                    background:    highlighted ? '#fef3c7' : '#fff',
                    border:        `2px solid ${highlighted ? '#f59e0b' : '#e5e7eb'}`,
                    borderRadius:  12,
                    padding:       '12px 16px',
                    width:         160,
                    textAlign:     'center',
                    boxShadow:     '0 2px 8px rgba(0,0,0,0.06)',
                    position:      'relative',
                    cursor:        hasChildren ? 'pointer' : 'default',
                    transition:    'all 0.2s',
                }}
                onClick={() => hasChildren && setExpanded(p => !p)}>

                {/* Avatar */}
                <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: node.avatar ? 'transparent' : roleColor,
                    margin: '0 auto 8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 700, color: '#fff',
                }}>
                    {node.avatar
                        ? <img src={node.avatar} alt=""
                            style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }}/>
                        : initials(node.name)
                    }
                </div>

                {/* Name */}
                <div style={{ fontWeight: 700, fontSize: 12, lineHeight: 1.3, marginBottom: 3 }}>
                    {node.name}
                </div>

                {/* Designation */}
                {node.designation && (
                    <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>
                        {node.designation}
                    </div>
                )}

                {/* Department badge */}
                {node.department && (
                    <div style={{
                        display: 'inline-block',
                        background: roleColor + '20',
                        color: roleColor,
                        fontSize: 9, fontWeight: 600,
                        padding: '2px 8px', borderRadius: 10,
                    }}>
                        {node.department}
                    </div>
                )}

                {/* Employee code */}
                {node.employee_code && (
                    <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 4 }}>
                        {node.employee_code}
                    </div>
                )}

                {/* Expand/collapse indicator */}
                {hasChildren && (
                    <div style={{
                        position: 'absolute', bottom: -10, left: '50%',
                        transform: 'translateX(-50%)',
                        background: roleColor, color: '#fff',
                        width: 20, height: 20, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700,
                        boxShadow: '0 2px 4px rgba(0,0,0,.2)',
                    }}>
                        {expanded ? '−' : `+${node.children.length}`}
                    </div>
                )}
            </div>

            {/* Children */}
            {hasChildren && expanded && (
                <div style={{ marginTop: 24 }}>
                    {/* Vertical line down */}
                    <div style={{ width: 2, height: 16, background: '#d1d5db', margin: '0 auto' }}/>

                    {/* Horizontal line across children */}
                    {node.children.length > 1 && (
                        <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                            <div style={{
                                position: 'absolute', top: 0,
                                left: `calc(50% - ${node.children.length * 90}px)`,
                                width: node.children.length * 180,
                                height: 2, background: '#d1d5db',
                            }}/>
                        </div>
                    )}

                    {/* Children row */}
                    <div style={{ display: 'flex', gap: 24, justifyContent: 'center', paddingTop: 2 }}>
                        {node.children.map(child => (
                            <div key={child.id}
                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* Vertical line up to child */}
                                <div style={{ width: 2, height: 14, background: '#d1d5db' }}/>
                                <OrgNode node={child} depth={depth + 1} searchTerm={searchTerm}/>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════════════════
const OrgChart = () => {
    const [tree,    setTree]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [search,  setSearch]  = useState('');
    const [stats,   setStats]   = useState({ total: 0, departments: 0 });

    useEffect(() => {
        axiosInstance.get('/employees', { params: { limit: 500 } })
            .then(r => {
                const raw  = r.data?.data?.employees || r.data?.data || r.data || [];
                const list = Array.isArray(raw) ? raw : [];

                // ── FIX: exclude On Hold (inactive) employees ─────────────
                const activeOnly = list.filter(e =>
                    (e.status || 'active') === 'active'
                );

                const emps = activeOnly.map(e => ({
                    id:            e.id || e.user_id,
                    name:          `${e.first_name || ''} ${e.last_name || ''}`.trim() || 'Unknown',
                    designation:   e.designation_name || e.designation || '',
                    department:    e.department_name  || e.department  || '',
                    manager_id:    e.manager_id       || null,
                    employee_code: e.employee_code    || '',
                    avatar:        e.avatar           || null,
                    role:          e.role_name        || e.role || 'employee',
                    status:        e.status           || 'active',
                }));

                setTree(buildTree(emps));

                const deptCount = new Set(emps.map(e => e.department).filter(Boolean)).size;
                setStats({ total: emps.length, departments: deptCount });
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="p-2 p-md-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <Users size={22} className="me-2 text-primary"/>Organisation Chart
                    </h4>
                    <p className="text-muted small mb-0">
                        {stats.total} active employees · {stats.departments} departments
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <div style={{ position: 'relative' }}>
                        <Search size={13} style={{
                            position: 'absolute', left: 10, top: '50%',
                            transform: 'translateY(-50%)', color: '#9ca3af',
                        }}/>
                        <input
                            type="text"
                            placeholder="Search employees…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                paddingLeft: 30, paddingRight: 12,
                                paddingTop: 6, paddingBottom: 6,
                                fontSize: 13, border: '1px solid #e5e7eb',
                                borderRadius: 8, outline: 'none', width: 200,
                            }}
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary"/>
                    <p className="text-muted mt-2 small">Loading org chart…</p>
                </div>
            ) : tree.length === 0 ? (
                <Card className="border-0 shadow-sm p-5 text-center text-muted">
                    <Users size={40} className="mx-auto mb-2 opacity-30"/>
                    <p>No active employees found. Add employees to see the org chart.</p>
                </Card>
            ) : (
                <>
                    {/* Legend */}
                    <div className="d-flex gap-3 flex-wrap mb-4" style={{ fontSize: 11 }}>
                        {Object.entries(ROLE_COLOURS)
                            .filter(([r]) => r !== 'super-admin')
                            .map(([role, color]) => (
                                <div key={role} className="d-flex align-items-center gap-1">
                                    <div style={{
                                        width: 12, height: 12,
                                        borderRadius: '50%', background: color,
                                    }}/>
                                    <span className="text-muted" style={{ textTransform: 'capitalize' }}>
                                        {role.replace(/-/g, ' ')}
                                    </span>
                                </div>
                            ))}
                    </div>

                    {/* Chart — horizontally scrollable */}
                    <div style={{
                        overflowX: 'auto', overflowY: 'auto',
                        padding: 32, background: '#f8fafc', borderRadius: 12,
                        minHeight: 400,
                    }}>
                        <div style={{
                            display: 'flex', gap: 48,
                            justifyContent: 'center', paddingBottom: 32,
                        }}>
                            {tree.map(root => (
                                <OrgNode key={root.id} node={root} depth={0} searchTerm={search}/>
                            ))}
                        </div>
                    </div>
                    <p className="text-muted mt-3 small">
                        Click a card with children to expand / collapse.
                        Search highlights matching employees.
                        On Hold employees are not shown.
                    </p>
                </>
            )}
        </div>
    );
};

export default OrgChart;
